import { Image as ImageIcon, Loader2, Palette } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "../../contexts/LanguageContext";
import Button from "../ui/Button";
import Modal from "../ui/Modal";
import Select from "../ui/Select";
import Textarea from "../ui/Textarea";

import {
  IMAGE_PROVIDER_OPTIONS,
  imageProviderHint,
} from "../../shared/imageProviders";

interface EnhanceImageModalProps {
  open: boolean;
  onClose: () => void;
  postId: number | null;
  imageUrl: string;
  onConfirm: (
    postId: number,
    restrictions?: string,
    provider?: string,
  ) => Promise<void>;
  loading: boolean;
}

const EnhanceImageModal: React.FC<EnhanceImageModalProps> = ({
  open,
  onClose,
  postId,
  imageUrl,
  onConfirm,
  loading,
}) => {
  const { t } = useTranslation();
  const [restrictions, setRestrictions] = useState("");
  const [provider, setProvider] = useState<string>("dalle");

  useEffect(() => {
    if (open) {
      setRestrictions("");
      setProvider("dalle");
    }
  }, [open]);

  const handleClose = () => {
    setRestrictions("");
    onClose();
  };

  const handleConfirm = async () => {
    if (postId == null) return;
    await onConfirm(postId, restrictions.trim() || undefined, provider);
    setRestrictions("");
  };

  if (postId == null) return null;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t("Melhorar Imagem com IA - Post #{postId}", { postId })}
      maxWidth="max-w-2xl"
      footer={
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
            size="sm"
          >
            {t("Cancelar")}
          </Button>
          <Button
            type="button"
            variant="primary"
            icon={
              loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Palette className="h-4 w-4" />
              )
            }
            onClick={handleConfirm}
            disabled={loading}
            size="sm"
          >
            {loading ? t("Melhorando...") : t("Melhorar Imagem")}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          {t(
            "A imagem atual do post será usada como referência. A IA irá melhorá-la com base no conteúdo e nas instruções abaixo.",
          )}
        </p>

        <div className="space-y-2">
          <Select
            label={t("Provedor de IA")}
            options={[...IMAGE_PROVIDER_OPTIONS]}
            value={provider}
            onChange={setProvider}
            size="md"
            disabled={loading}
          />
          <p className="text-xs text-gray-500">
            {imageProviderHint(provider) &&
              `✨ ${t(imageProviderHint(provider))}`}
          </p>
        </div>

        <div className="space-y-2">
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">
            {t("Imagem atual (referência)")}
          </label>
          <div className="rounded-lg border-2 border-purple-200 bg-gray-50 p-3 flex justify-center items-center min-h-[200px]">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={t("Imagem do post")}
                className="max-h-[280px] w-auto object-contain rounded-sm"
              />
            ) : (
              <div className="flex flex-col items-center gap-2 text-gray-500">
                <ImageIcon className="h-12 w-12" />
                <span className="text-sm">{t("Nenhuma imagem carregada")}</span>
              </div>
            )}
          </div>
        </div>

        <Textarea
          label={t("Restrições e Instruções (Opcional)")}
          value={restrictions}
          onChange={(e) => setRestrictions(e.target.value)}
          placeholder={t(
            "Ex: não incluir texto na imagem, manter as cores da marca, adicionar mais contraste...",
          )}
          inputSize="md"
          disabled={loading}
          hint={t(
            "Instruções específicas para a melhoria. O backend usa o conteúdo do post como contexto; deixe vazio para usar apenas o contexto automático.",
          )}
        />
      </div>
    </Modal>
  );
};

export default EnhanceImageModal;
